import React, { useState } from 'react';
import { db } from '../../firebase';
import { useAuthorDashboard } from '../AuthorDashboardContext';
import {
  doc, addDoc, collection, updateDoc, deleteDoc, increment, serverTimestamp
} from 'firebase/firestore';
import { Card, CardContent } from '../../components/ui/card';
import {
  Star, CheckCircle, XCircle, Trash2, Edit2, ExternalLink, Lock, Plus, X
} from 'lucide-react';

/* ----------------------------------------
   MAIN COMPONENT
---------------------------------------- */
const REVIEW_PLATFORMS = [
  'Amazon', 'Goodreads', 'Instagram', 'TikTok', 'Blog',
  'BookBub', 'StoryGraph', 'NetGalley', 'Fable',
];

export default function AuthorReviewsTab() {
  const { readers, reviews, books, basePath } = useAuthorDashboard();

  // Section collapse state
  const [sectionOpen, setSectionOpen] = useState({ pending: true, approved: false, rejected: false });

  // Manual review modal
  const [showAddReview, setShowAddReview] = useState(false);
  const [manualReview, setManualReview] = useState({
    readerId: '', bookId: '', rating: 0, platform: '', reviewLink: '', fullReview: '', privateNotes: '',
  });
  const [isSavingManual, setIsSavingManual] = useState(false);

  // Editable ratings
  const [editingRatings, setEditingRatings] = useState({});

  // Custom points per review
  const [customPoints, setCustomPoints] = useState({});

  // Approval modal state
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalReview, setApprovalReview] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState('Street Team');

  /* ─── Review Handlers ──── */

  const handleApproveReview = async (reviewId, reviewData, pointsToAward, newReaderStatus) => {
    const points = pointsToAward !== undefined ? pointsToAward : 50;
    const statusMap = {
      'Street Team': 'Street Team',
      'Second ARC': 'Book 2 ARC interest',
      'Closed': 'Closed',
    };
    const finalStatus = statusMap[newReaderStatus] || 'Street Team';

    try {
      await updateDoc(doc(db, basePath, 'bookReviews', reviewId), {
        status: 'approved',
        approvedAt: serverTimestamp(),
        pointsAwarded: points,
      });

      const readerRef = readers.find(r =>
        r.emailCorrespondence === reviewData.readerEmail || r.id === reviewData.readerId
      );
      if (readerRef) {
        const readerUpdate = {
          totalPoints: increment(points),
          totalReviews: increment(1),
          status: finalStatus,
          reviewRating: reviewData.rating || null,
          lastModified: serverTimestamp(),
        };
        await updateDoc(doc(db, 'readers', readerRef.id), readerUpdate);

        // Also update per-book reader subcollection so Author Portal reflects the change
        if (reviewData.bookId) {
          try {
            await updateDoc(doc(db, basePath, 'books', reviewData.bookId, 'readers', readerRef.id), readerUpdate);
          } catch (_) { /* per-book doc may not exist yet */ }
        }
      }

      setCustomPoints(prev => { const u = { ...prev }; delete u[reviewId]; return u; });
      alert(`✅ Review approved! ${points} points awarded. ${readerRef?.name || 'Reader'} moved to "${finalStatus}".`);
    } catch (error) {
      alert('❌ Error approving review: ' + error.message);
    }
  };

  const handleRejectReview = async (reviewId) => {
    if (!window.confirm('Reject and delete this review? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, basePath, 'bookReviews', reviewId));
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleDeleteReview = async (reviewId, reviewData) => {
    if (!window.confirm('Are you sure you want to delete this review? This cannot be undone.')) return;
    try {
      if (reviewData.status === 'approved' && reviewData.pointsAwarded > 0) {
        const reader = readers.find(r =>
          r.emailCorrespondence === reviewData.readerEmail || r.id === reviewData.readerId
        );
        if (reader) {
          await updateDoc(doc(db, 'readers', reader.id), {
            totalPoints: increment(-reviewData.pointsAwarded),
            totalReviews: increment(-1),
            lastModified: serverTimestamp(),
          });
        }
      }
      await deleteDoc(doc(db, basePath, 'bookReviews', reviewId));
      alert('✅ Review deleted successfully!');
    } catch (error) {
      alert('❌ Error deleting review: ' + error.message);
    }
  };

  const handleUpdateReviewRating = async (reviewId, newRating) => {
    try {
      await updateDoc(doc(db, basePath, 'bookReviews', reviewId), { rating: newRating });
      setEditingRatings(prev => { const u = { ...prev }; delete u[reviewId]; return u; });
    } catch (error) {
      alert('❌ Error updating rating: ' + error.message);
    }
  };

  const handleBackfillRatings = async () => {
    if (!window.confirm('This will copy star ratings from all approved reviews onto the reader records so filters work. Continue?')) return;
    const approvedRevs = reviews.filter(r => r.status === 'approved' && r.rating);
    if (approvedRevs.length === 0) { alert('No approved reviews with ratings found.'); return; }
    let updated = 0;
    for (const rev of approvedRevs) {
      const reader = readers.find(r => r.emailCorrespondence === rev.readerEmail || r.id === rev.readerId);
      if (reader) {
        try {
          await updateDoc(doc(db, 'readers', reader.id), { reviewRating: rev.rating, lastModified: serverTimestamp() });
          updated++;
        } catch { /* skip */ }
      }
    }
    alert(`✅ Done! Updated ${updated} reader record${updated !== 1 ? 's' : ''} with star ratings.`);
  };

  /* ─── Add Manual Review ──── */

  function openAddReview() {
    setManualReview({
      readerId: '', bookId: books[0]?.id || '', rating: 0,
      platform: '', reviewLink: '', fullReview: '', privateNotes: '',
    });
    setShowAddReview(true);
  }

  async function handleSaveManualReview() {
    const { readerId, bookId, rating, platform, reviewLink, fullReview, privateNotes } = manualReview;
    if (!readerId) { alert('Please select a reader'); return; }
    if (!bookId) { alert('Please select a book'); return; }
    if (!rating) { alert('Please set a star rating'); return; }

    setIsSavingManual(true);
    try {
      const reader = readers.find(r => r.id === readerId);
      const book = books.find(b => b.id === bookId);

      const reviewLinks = {};
      if (platform && reviewLink) {
        reviewLinks[platform.toLowerCase()] = reviewLink;
      }

      await addDoc(collection(db, basePath, 'bookReviews'), {
        readerId,
        readerEmail: reader?.emailCorrespondence || '',
        readerName: reader?.name || 'Unknown',
        bookId,
        bookTitle: book?.title || 'Unknown',
        rating: Number(rating),
        fullReview: fullReview || '',
        privateNotes: privateNotes || '',
        reviewLinks,
        status: 'approved',
        approvedAt: serverTimestamp(),
        submittedAt: serverTimestamp(),
        pointsAwarded: 0,
        manualEntry: true,
      });

      // Update reader record with rating
      if (reader) {
        await updateDoc(doc(db, 'readers', reader.id), {
          reviewRating: Number(rating),
          totalReviews: increment(1),
          status: 'Reviewed',
          lastModified: serverTimestamp(),
        });
      }

      setShowAddReview(false);
      alert('✅ Review added successfully!');
    } catch (error) {
      alert('❌ Error adding review: ' + error.message);
    }
    setIsSavingManual(false);
  }

  /* ─── Helpers ──── */

  const getReaderName = (rev) => {
    const r = readers.find(r => r.emailCorrespondence === rev.readerEmail || r.id === rev.readerId);
    return r?.name || rev.readerName || 'Unknown Reader';
  };

  const pathToStatus = {
    'street-team': 'Street Team',
    'arc-priority': 'Second ARC',
    'not-for-me': 'Closed',
  };

  /* ─── Review Card ──── */

  const renderReviewCard = (rev) => {
    const submittedLinks = Object.entries(rev.reviewLinks || {})
      .filter(([, link]) => link && link.trim() !== '');
    const currentRating = editingRatings[rev.id] !== undefined ? editingRatings[rev.id] : (rev.rating || 0);
    const isEditingRating = editingRatings[rev.id] !== undefined;

    return (
      <div key={rev.id} className="rounded-xl border-2 border-slate-200 bg-white p-5 shadow-sm">
        {/* Header */}
        <div className="mb-4 flex items-start justify-between border-b border-slate-100 pb-3">
          <div>
            <p className="text-sm font-medium text-slate-600">
              📅 Submitted: {rev.submittedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
            </p>
            {rev.readerPath && (
              <p className="mt-1 text-sm font-semibold text-purple-600">
                {rev.readerPath === 'street-team' && '🎯 Wants to join Street Team!'}
                {rev.readerPath === 'arc-priority' && '📚 Wants future ARCs'}
                {rev.readerPath === 'not-for-me' && 'Not interested in future content'}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {rev.status === 'approved' && (
              <button onClick={() => handleDeleteReview(rev.id, rev)}
                className="rounded-lg border border-red-300 bg-red-50 p-2 text-red-700 hover:bg-red-100 transition-colors" title="Delete review">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Editable Star Rating */}
        <div className="mb-4 rounded-lg bg-gradient-to-r from-slate-50 to-orange-50 p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-900">⭐ Rating:</p>
            {!isEditingRating ? (
              <button onClick={() => setEditingRatings(prev => ({ ...prev, [rev.id]: rev.rating || 0 }))}
                className="flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs font-medium text-teal-700 hover:bg-slate-200 transition-colors">
                <Edit2 className="h-3 w-3" /> Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => handleUpdateReviewRating(rev.id, editingRatings[rev.id])}
                  className="rounded-lg bg-green-100 px-2 py-1 text-xs font-semibold text-green-700 hover:bg-green-200">Save</button>
                <button onClick={() => setEditingRatings(prev => { const u = {...prev}; delete u[rev.id]; return u; })}
                  className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200">Cancel</button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map(star => (
              <button key={star} type="button"
                onClick={() => isEditingRating && setEditingRatings(prev => ({ ...prev, [rev.id]: star }))}
                className={`focus:outline-none transition-transform ${isEditingRating ? 'hover:scale-110 cursor-pointer' : 'cursor-default'}`}>
                <Star className={`h-6 w-6 transition-colors ${star <= currentRating ? 'fill-teal-400 text-teal-400' : 'text-slate-300'}`} />
              </button>
            ))}
            <span className="ml-2 text-lg font-bold text-teal-600">{currentRating}/5</span>
            {isEditingRating && <span className="text-xs text-slate-500 italic ml-1">editing...</span>}
          </div>
        </div>

        {rev.quickSummary && (
          <div className="mb-4 rounded-lg bg-blue-50 p-4 border-l-4 border-blue-400">
            <p className="text-xs font-bold text-blue-900 mb-2">📝 Quick Summary</p>
            <p className="text-sm text-blue-800 leading-relaxed">{rev.quickSummary}</p>
          </div>
        )}
        {rev.lovedMost && (
          <div className="mb-4 rounded-lg bg-pink-50 p-4 border-l-4 border-pink-400">
            <p className="text-xs font-bold text-pink-900 mb-2">💖 What They Loved Most</p>
            <p className="text-sm font-semibold text-pink-800">{rev.lovedMost}</p>
            {rev.lovedMostDetails && <p className="text-sm text-pink-700 mt-2 leading-relaxed">{rev.lovedMostDetails}</p>}
          </div>
        )}
        {rev.favoriteCharacterMoment && (
          <div className="mb-4 rounded-lg bg-purple-50 p-4 border-l-4 border-purple-400">
            <p className="text-xs font-bold text-purple-900 mb-2">⭐ Favorite Character Moment</p>
            <p className="text-sm text-purple-800 leading-relaxed">{rev.favoriteCharacterMoment}</p>
          </div>
        )}
        {rev.targetAudience && rev.targetAudience.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-bold text-slate-700 mb-2">👥 Perfect For:</p>
            <div className="flex flex-wrap gap-2">
              {rev.targetAudience.map((a, i) => (
                <span key={i} className="rounded-full bg-indigo-100 px-3 py-1 text-sm font-medium text-indigo-700">{a}</span>
              ))}
            </div>
          </div>
        )}
        {rev.fullReview && (
          <div className="mb-4 rounded-lg bg-slate-50 p-4">
            <p className="text-xs font-bold text-slate-900 mb-3">📖 Full Review</p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{rev.fullReview}</p>
          </div>
        )}
        {rev.favoriteQuote && (
          <div className="mb-4 rounded-lg border-l-4 border-slate-500 bg-slate-50 p-4">
            <p className="text-xs font-bold text-slate-900 mb-2">💬 Favorite Quote</p>
            <p className="text-sm italic text-slate-800">"{rev.favoriteQuote}"</p>
          </div>
        )}
        {submittedLinks.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-bold text-slate-700 mb-3">🔗 Posted Reviews</p>
            <div className="flex flex-wrap gap-2">
              {submittedLinks.map(([platform, link]) => (
                <a key={platform} href={link} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-slate-500 to-orange-500 px-4 py-2 text-sm font-semibold text-white hover:from-teal-600 hover:to-orange-600 transition-all shadow-sm">
                  {platform}<ExternalLink size={14} />
                </a>
              ))}
            </div>
          </div>
        )}
        {rev.privateNotes && (
          <div className="mb-4 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="h-4 w-4 text-slate-600" />
              <p className="text-xs font-bold text-slate-700">Private Notes (Only You See This)</p>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">{rev.privateNotes}</p>
          </div>
        )}
        {rev.readerComments && !rev.fullReview && (
          <div className="mb-4 rounded-lg bg-slate-100 p-3">
            <p className="text-xs font-medium text-slate-600 mb-1">💬 Comments:</p>
            <p className="text-sm text-slate-700">{rev.readerComments}</p>
          </div>
        )}

        {/* ACTION SECTION */}
        <div className="mt-5 border-t border-slate-200 pt-4">
          {(!rev.status || rev.status === 'pending') && (
            <div className="space-y-3">
              <div className="rounded-lg bg-green-50 p-4">
                <label className="block text-sm font-semibold text-green-900 mb-2">💰 Points to Award</label>
                <div className="flex items-center gap-3">
                  <input type="number" min="0" step="10" placeholder="50"
                    value={customPoints[rev.id] !== undefined ? customPoints[rev.id] : 50}
                    onChange={(e) => setCustomPoints({...customPoints, [rev.id]: Number(e.target.value)})}
                    className="w-32 rounded-lg border-2 border-green-300 px-3 py-2 text-center text-lg font-bold focus:border-green-500 focus:ring-2 focus:ring-green-200"
                  />
                  <p className="text-xs text-green-600">Suggested: 50 for a review submission</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const suggestedStatus = pathToStatus[rev.readerPath] || 'Street Team';
                    setApprovalReview({ id: rev.id, data: rev, points: customPoints[rev.id] !== undefined ? customPoints[rev.id] : 50 });
                    setApprovalStatus(suggestedStatus);
                    setShowApprovalModal(true);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-3 text-sm font-bold text-white hover:from-green-700 hover:to-emerald-700 transition-all shadow-md">
                  <CheckCircle size={18} /> Accept Review
                </button>
                <button onClick={() => handleRejectReview(rev.id)}
                  className="flex items-center justify-center gap-2 rounded-lg border-2 border-red-300 bg-white px-6 py-3 text-sm font-bold text-red-700 hover:bg-red-50 transition-all">
                  <XCircle size={18} /> Reject
                </button>
              </div>
            </div>
          )}
          {rev.status === 'approved' && (
            <div className="flex items-center gap-3 rounded-lg bg-green-50 p-4">
              <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-green-900">✅ Review Approved</p>
                <p className="text-sm text-green-700">+{rev.pointsAwarded || 0} points awarded to reader</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  /* ─── Section Header ──── */

  const SectionHeader = ({ label, count, color, bg, sectionKey }) => (
    <button
      onClick={() => setSectionOpen(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }))}
      className={`w-full flex items-center justify-between rounded-2xl border-2 px-5 py-4 font-bold text-left transition-all ${bg}`}
    >
      <div className="flex items-center gap-3">
        <span className={`text-lg font-bold ${color}`}>{label}</span>
        <span className={`rounded-full px-3 py-1 text-sm font-bold ${color} bg-white/60`}>{count}</span>
      </div>
      <span className={`text-xl ${color}`}>{sectionOpen[sectionKey] ? '▲' : '▼'}</span>
    </button>
  );

  /* ─── Split reviews by status ──── */

  const pendingRevs = reviews.filter(r => !r.status || r.status === 'pending');
  const approvedRevs = reviews.filter(r => r.status === 'approved');
  const rejectedRevs = reviews.filter(r => r.status === 'rejected');

  /* ========================================
     RENDER
  ======================================== */
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900">Review Submissions</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={openAddReview}
            className="flex items-center gap-2 rounded-lg bg-teal-600 px-3 py-2 text-xs font-semibold text-white hover:bg-teal-700 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Review
          </button>
          <button
            onClick={handleBackfillRatings}
            className="flex items-center gap-2 rounded-lg border border-teal-300 bg-slate-50 px-3 py-2 text-xs font-semibold text-teal-700 hover:bg-slate-100 transition-colors"
            title="Copy star ratings from approved reviews onto reader records so the star filter works"
          >
            <Star className="h-3.5 w-3.5" />
            Sync Ratings to Readers
          </button>
        </div>
      </div>

      {reviews.length === 0 ? (
        <div className="rounded-2xl border border-slate-100 bg-white p-6 text-center text-sm text-slate-500">
          No reviews submitted yet.
        </div>
      ) : (
        <div className="space-y-4">
          {/* PENDING */}
          <div>
            <SectionHeader label="⏳ Pending Review" count={pendingRevs.length} color="text-yellow-800" bg="border-yellow-300 bg-yellow-50 hover:bg-yellow-100" sectionKey="pending" />
            {sectionOpen.pending && pendingRevs.length > 0 && (
              <div className="mt-3 space-y-4 pl-2">
                {pendingRevs.map(rev => (
                  <div key={rev.id}>
                    <p className="mb-2 text-sm font-semibold text-slate-600">👤 {getReaderName(rev)} — {rev.bookTitle || 'Unknown Book'}</p>
                    {renderReviewCard(rev)}
                  </div>
                ))}
              </div>
            )}
            {sectionOpen.pending && pendingRevs.length === 0 && (
              <p className="mt-3 pl-2 text-sm text-slate-400">No pending reviews.</p>
            )}
          </div>

          {/* ACCEPTED */}
          <div>
            <SectionHeader label="✅ Accepted" count={approvedRevs.length} color="text-green-800" bg="border-green-300 bg-green-50 hover:bg-green-100" sectionKey="approved" />
            {sectionOpen.approved && approvedRevs.length > 0 && (
              <div className="mt-3 space-y-4 pl-2">
                {approvedRevs.map(rev => (
                  <div key={rev.id}>
                    <p className="mb-2 text-sm font-semibold text-slate-600">👤 {getReaderName(rev)} — {rev.bookTitle || 'Unknown Book'}</p>
                    {renderReviewCard(rev)}
                  </div>
                ))}
              </div>
            )}
            {sectionOpen.approved && approvedRevs.length === 0 && (
              <p className="mt-3 pl-2 text-sm text-slate-400">No accepted reviews yet.</p>
            )}
          </div>

          {/* REJECTED */}
          <div>
            <SectionHeader label="❌ Rejected" count={rejectedRevs.length} color="text-red-800" bg="border-red-200 bg-red-50 hover:bg-red-100" sectionKey="rejected" />
            {sectionOpen.rejected && rejectedRevs.length > 0 && (
              <div className="mt-3 space-y-4 pl-2">
                {rejectedRevs.map(rev => (
                  <div key={rev.id}>
                    <p className="mb-2 text-sm font-semibold text-slate-600">👤 {getReaderName(rev)} — {rev.bookTitle || 'Unknown Book'}</p>
                    {renderReviewCard(rev)}
                  </div>
                ))}
              </div>
            )}
            {sectionOpen.rejected && rejectedRevs.length === 0 && (
              <p className="mt-3 pl-2 text-sm text-slate-400">No rejected reviews.</p>
            )}
          </div>
        </div>
      )}

      {/* REVIEW APPROVAL MODAL */}
      {showApprovalModal && approvalReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl">
            <h3 className="mb-1 text-xl font-bold text-slate-900">✅ Accept Review</h3>
            <p className="mb-5 text-sm text-slate-500">
              Choose what happens to this reader's status after you accept their review.
              The path they selected has been pre-selected for you.
            </p>

            {/* Points */}
            <div className="mb-4 rounded-xl bg-green-50 p-4">
              <label className="mb-2 block text-sm font-semibold text-green-900">💰 Points to Award</label>
              <div className="flex items-center gap-3">
                <input type="number" min="0" step="10"
                  value={approvalReview.points}
                  onChange={(e) => setApprovalReview(prev => ({ ...prev, points: Number(e.target.value) }))}
                  className="w-28 rounded-lg border-2 border-green-300 px-3 py-2 text-center text-lg font-bold focus:border-green-500 focus:ring-2 focus:ring-green-200"
                />
                <p className="text-sm text-green-700">points for this review</p>
              </div>
            </div>

            {/* Status Selection */}
            <div className="mb-5">
              <p className="mb-3 text-sm font-semibold text-slate-700">📋 Move Reader To:</p>
              <div className="space-y-2">
                {[
                  { value: 'Street Team', label: '🎉 Street Team', desc: 'They want to help create social buzz & launch energy', color: 'border-teal-400 bg-slate-50' },
                  { value: 'Second ARC', label: '📚 Second ARC', desc: 'They want priority access to future ARCs', color: 'border-indigo-400 bg-indigo-50' },
                  { value: 'Closed', label: '🔒 Closed', desc: 'No further engagement needed', color: 'border-slate-400 bg-slate-50' },
                ].map(opt => (
                  <label key={opt.value}
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 p-3 transition-all ${
                      approvalStatus === opt.value ? opt.color + ' shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}>
                    <input type="radio" name="approvalStatus" value={opt.value}
                      checked={approvalStatus === opt.value}
                      onChange={(e) => setApprovalStatus(e.target.value)}
                      className="mt-0.5 h-4 w-4 text-teal-600 focus:ring-slate-500"
                    />
                    <div>
                      <p className="text-sm font-bold text-slate-900">{opt.label}</p>
                      <p className="text-xs text-slate-500">{opt.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={async () => {
                  await handleApproveReview(approvalReview.id, approvalReview.data, approvalReview.points, approvalStatus);
                  setShowApprovalModal(false);
                  setApprovalReview(null);
                }}
                className="flex-1 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 py-3 text-sm font-bold text-white hover:from-green-700 hover:to-emerald-700 transition-all shadow-md">
                ✅ Confirm & Accept
              </button>
              <button onClick={() => { setShowApprovalModal(false); setApprovalReview(null); }}
                className="rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD MANUAL REVIEW MODAL */}
      {showAddReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-slate-900">Add Review Manually</h3>
              <button onClick={() => setShowAddReview(false)} className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Reader */}
            <div className="mb-4">
              <label className="mb-1 block text-sm font-semibold text-slate-700">Reader</label>
              <select value={manualReview.readerId}
                onChange={(e) => setManualReview(prev => ({ ...prev, readerId: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200">
                <option value="">Select a reader...</option>
                {readers.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(r => (
                  <option key={r.id} value={r.id}>{r.name || r.emailCorrespondence}</option>
                ))}
              </select>
            </div>

            {/* Book */}
            <div className="mb-4">
              <label className="mb-1 block text-sm font-semibold text-slate-700">Book</label>
              <select value={manualReview.bookId}
                onChange={(e) => setManualReview(prev => ({ ...prev, bookId: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200">
                {books.map(b => (
                  <option key={b.id} value={b.id}>{b.title}</option>
                ))}
              </select>
            </div>

            {/* Star Rating */}
            <div className="mb-4">
              <label className="mb-2 block text-sm font-semibold text-slate-700">Rating</label>
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map(star => (
                  <button key={star} type="button"
                    onClick={() => setManualReview(prev => ({ ...prev, rating: star }))}
                    className="focus:outline-none hover:scale-110 transition-transform">
                    <Star className={`h-8 w-8 transition-colors ${star <= manualReview.rating ? 'fill-teal-400 text-teal-400' : 'text-slate-300'}`} />
                  </button>
                ))}
                {manualReview.rating > 0 && (
                  <span className="ml-2 text-lg font-bold text-teal-600">{manualReview.rating}/5</span>
                )}
              </div>
            </div>

            {/* Platform */}
            <div className="mb-4">
              <label className="mb-1 block text-sm font-semibold text-slate-700">Platform</label>
              <select value={manualReview.platform}
                onChange={(e) => setManualReview(prev => ({ ...prev, platform: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200">
                <option value="">Select platform...</option>
                {REVIEW_PLATFORMS.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            {/* Review Link */}
            {manualReview.platform && (
              <div className="mb-4">
                <label className="mb-1 block text-sm font-semibold text-slate-700">Review Link</label>
                <input type="url" value={manualReview.reviewLink}
                  onChange={(e) => setManualReview(prev => ({ ...prev, reviewLink: e.target.value }))}
                  placeholder={`https://${manualReview.platform.toLowerCase()}.com/...`}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                />
              </div>
            )}

            {/* What they said */}
            <div className="mb-4">
              <label className="mb-1 block text-sm font-semibold text-slate-700">What they said</label>
              <textarea value={manualReview.fullReview}
                onChange={(e) => setManualReview(prev => ({ ...prev, fullReview: e.target.value }))}
                rows={4} placeholder="Paste or type the reader's review text..."
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200 leading-relaxed"
              />
            </div>

            {/* Private Notes */}
            <div className="mb-5">
              <label className="mb-1 block text-sm font-semibold text-slate-700">Private Notes <span className="font-normal text-slate-400">(only you see this)</span></label>
              <textarea value={manualReview.privateNotes}
                onChange={(e) => setManualReview(prev => ({ ...prev, privateNotes: e.target.value }))}
                rows={2} placeholder="Any notes for yourself..."
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200 leading-relaxed"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button onClick={handleSaveManualReview} disabled={isSavingManual}
                className="flex-1 rounded-lg bg-teal-600 py-3 text-sm font-bold text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {isSavingManual ? 'Saving...' : 'Save Review'}
              </button>
              <button onClick={() => setShowAddReview(false)} disabled={isSavingManual}
                className="rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
